import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import SearchInput from "common/components/SearchInput";
import {
  DropdownMenuContent,
  DropdownMenuList,
} from "common/components/ui/DropdownMenu";
import useDebounce from "common/hooks/useDebounce";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { GenericSearchParam, ProfileSearchParams } from "common/types";

import { PROFILE_SEARCH_OPTIONS } from "../../../../../../constants";
import useSearchOptions from "../../../../../../datahooks/useSearchOptions";
import { useSearchParamsContext } from "../../SearchParamsContext";
import useMergedOptions from "../useMergedOptions";
import useSearchParams from "../useSearchParams";
import SearchParamOption from "./SearchParamOption";

interface GenericOptionsProps {
  optionsKey: keyof Omit<ProfileSearchParams, "keywords" | "followers_of">;
}

export default function GenericOptions({ optionsKey }: GenericOptionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { label } = PROFILE_SEARCH_OPTIONS[optionsKey];

  const [inputValue, setInputValue] = useState("");
  const [debouncedValue] = useDebounce(inputValue, 300);

  const { searchParams } = useSearchParamsContext();

  const {
    addSearchParamValue,
    removeSearchParamValue,
    setSearchParamValue,
    appliedSearchParamValues,
  } = useSearchParams(optionsKey);

  const searchKey =
    optionsKey === "current_companies" || optionsKey === "past_companies"
      ? "companies"
      : optionsKey;

  const { suggestions, ...searchOptions } = useSearchOptions({
    searchParams,
    [`${searchKey}Keywords`]: debouncedValue,
  });

  const searchResults = searchOptions[searchKey] || [];
  const suggestedOptions = suggestions ? suggestions[optionsKey] : [];

  const mergedOptions = useMergedOptions(
    appliedSearchParamValues as GenericSearchParam[],
    searchResults,
    suggestedOptions,
  );

  const listContent = mergedOptions.length ? (
    mergedOptions.map((option) => {
      const isSelected = !!appliedSearchParamValues.find(
        ({ urn }) => urn === option.urn,
      );

      function onToggle() {
        if (isSelected) {
          removeSearchParamValue(optionsKey, option);
        } else {
          addSearchParamValue(optionsKey, option);
        }
      }

      return (
        <SearchParamOption
          key={option.urn}
          isSelected={isSelected}
          onSelect={() => setSearchParamValue(optionsKey, [option])}
          onToggle={onToggle}
          option={option}
        />
      );
    })
  ) : (
    <p className="px-4 py-8 text-center text-black-500">No results found</p>
  );

  return isDesktop ? (
    <DropdownMenuContent className="w-80" align="start">
      <MenuSearchInput
        value={inputValue}
        placeholder={`Search ${label}`}
        onChange={(e) => setInputValue(e.currentTarget.value)}
      />
      <DropdownMenuList>{listContent}</DropdownMenuList>
    </DropdownMenuContent>
  ) : (
    <>
      <SearchInput
        value={inputValue}
        placeholder={`Search ${label}`}
        onChange={(e) => setInputValue(e.currentTarget.value)}
        onClear={() => setInputValue("")}
        className="mb-2"
      />
      <div className="flex flex-col">{listContent}</div>
    </>
  );
}
